/** A armação das telas por onde se entra no produto (DESIGN.md § 1.2).
 *
 * São três — entrar, criar a conta da empresa e aceitar um convite — e até
 * aqui só a primeira tinha desenho. As outras duas eram um formulário
 * centralizado no canvas vazio, e a pessoa que clicava em "Criar a conta da sua
 * empresa" saía de uma tela com painel de marca e caía em outra que parecia de
 * outro produto. Porta de entrada que muda de cara no meio do caminho é onde a
 * desconfiança começa, e é a única parte do sistema que alguém vê **antes** de
 * decidir confiar nele.
 *
 * O que se compartilha aqui é a **armação**, não a mensagem: as duas colunas, o
 * painel em verde de marca, o comportamento no mobile. O conteúdo do painel é
 * de cada tela, porque cada uma pega a pessoa num momento diferente — quem
 * entra já conhece o produto, quem cadastra está decidindo, quem aceita convite
 * foi trazido por alguém.
 *
 * **O painel some abaixo de `lg`.** Em 375px o que importa é o formulário, e
 * empurrá-lo para baixo da dobra por causa de uma apresentação é decisão que só
 * faz sentido no monitor de quem desenhou.
 */

import type { ReactNode } from "react";

interface Props {
  /** O painel de marca. Some no mobile — nada essencial pode morar aqui. */
  painel: ReactNode;
  /** O formulário. É o que sobra no mobile, e por isso vem sempre completo. */
  children: ReactNode;
}

export function PortalDeAcesso({ painel, children }: Props) {
  return (
    <main className="grid min-h-dvh grid-cols-1 bg-canvas lg:grid-cols-[1fr_minmax(420px,42%)]">
      <aside className="hidden flex-col justify-between bg-acao px-14 py-14 lg:flex">
        <p className="font-display text-title text-acao-contraste">Tanzuh</p>
        {painel}
        <p className="font-texto text-caption text-acao-contraste/60">
          Feito para quem vende conversando.
        </p>
      </aside>

      <section className="flex items-center justify-center px-6 py-14">
        <div className="flex w-full max-w-[420px] flex-col gap-6">{children}</div>
      </section>
    </main>
  );
}

/** O funil que o cadastro cria pronto — os nomes e as cores reais de
 * `pipeline.py`, não uma ilustração.
 *
 * É o que a pessoa recebe ao clicar em "Criar conta", e dizer isso com o
 * próprio vocabulário do board — bolinha na cor da etapa, nome em micro
 * maiúsculo — faz a tela seguinte parecer conhecida quando ela chegar lá.
 * Prometer com um desenho genérico e entregar outra coisa seria pior que não
 * prometer nada.
 */
export const ETAPAS_DO_FUNIL_PADRAO = [
  { nome: "Novo", cor: "#8AA6AD" },
  { nome: "Contato feito", cor: "#4E8A99" },
  { nome: "Proposta enviada", cor: "#E29B2B" },
  { nome: "Negociação", cor: "#C9762A" },
  { nome: "Ganho", cor: "#3F8F5B" },
] as const;

export function FunilDeAmostra() {
  return (
    <ul className="flex flex-col gap-2 rounded-lg bg-superficie p-5 shadow-e3">
      {ETAPAS_DO_FUNIL_PADRAO.map((etapa) => (
        <li key={etapa.nome} className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="h-[7px] w-[7px] shrink-0 rounded-full"
            style={{ backgroundColor: etapa.cor }}
          />
          <span className="font-texto text-micro font-bold uppercase tracking-wide text-texto">
            {etapa.nome}
          </span>
        </li>
      ))}
    </ul>
  );
}
